import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  X,
  User,
  Calendar,
  Clock,
  MapPin,
  Stethoscope,
  CheckCircle,
  Trash2,
  Edit,
  ThumbsUp,
} from "lucide-react";
import { format } from "date-fns";
import { getRoomName } from "@/utils/dataLookups";
import TelemedicineMeetingInfo from "@/components/booking/TelemedicineMeetingInfo";

export default function AppointmentDetailModal({
  appointment,
  services = [],
  rooms = [],
  onClose,
  onConfirm,
  onComplete,
  onDelete,
  onEdit,
}) {
  if (!appointment) return null;

  const getServiceDetails = (serviceId) => {
    if (!Array.isArray(services))
      return { service_name: "Unknown Service", description: "" };
    const service = services.find((s) => s.id === serviceId);
    return service || { service_name: "Unknown Service", description: "" };
  };

  const serviceDetails = getServiceDetails(appointment.service_id);

  const formatDateTime = (dateTimeString) => {
    try {
      const date = new Date(dateTimeString);
      return format(date, "EEEE, MMMM d, yyyy 'at' h:mm a");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid date";
    }
  };

  const formatTime = (dateTimeString) => {
    try {
      const date = new Date(dateTimeString);
      return format(date, "h:mm a");
    } catch (error) {
      console.error("Error formatting time:", error);
      return "Invalid time";
    }
  };

  const calculateEndTime = () => {
    // If we have an explicit end_time, use it
    if (appointment.end_time) {
      // Convert date and end_time to full datetime
      try {
        const appointmentDate = new Date(appointment.appointment_date);
        const [hours, minutes] = appointment.end_time.split(':');
        const endDateTime = new Date(appointmentDate);
        endDateTime.setHours(parseInt(hours), parseInt(minutes));
        return formatTime(endDateTime);
      } catch (error) {
        console.error("Error parsing end_time:", error);
      }
    }
    
    // Otherwise calculate from duration
    try {
      const startDate = new Date(appointment.appointment_date);
      const duration = appointment.duration_actual || 60; // Default to 60 minutes
      const endDate = new Date(startDate.getTime() + duration * 60000);
      return formatTime(endDate);
    } catch (error) {
      console.error("Error calculating end time:", error);
      return "Unknown";
    }
  };

  const statusColors = {
    scheduled: "bg-blue-100 text-blue-700",
    confirmed: "bg-teal-100 text-teal-700",
    in_progress: "bg-yellow-100 text-yellow-700",
    completed: "bg-green-100 text-green-700",
    cancelled: "bg-red-100 text-red-700",
    no_show: "bg-gray-100 text-gray-700",
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="appointment-detail-modal">
        <CardHeader>
          <div className="flex justify-between items-start">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Appointment Details
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status and Service */}
          <div className="flex items-center justify-between">
            <Badge
              className={`${statusColors[appointment.status] || statusColors.scheduled} border-0`}
              data-testid="appointment-status-badge"
            >
              {appointment.status?.replace("_", " ").toUpperCase()}
            </Badge>
            <div className="text-sm text-slate-500">ID: {appointment.id}</div>
          </div>

          {/* Service Information */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Stethoscope className="w-4 h-4 text-slate-500" />
              <div>
                <h3 className="font-medium text-slate-900">
                  {serviceDetails.service_name}
                </h3>
                {serviceDetails.description && (
                  <p className="text-sm text-slate-600">
                    {serviceDetails.description}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Appointment Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-500" />
                <div>
                  <p className="text-sm text-slate-600">Patient</p>
                  <p className="font-medium">{appointment.patient_id}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" />
                <div>
                  <p className="text-sm text-slate-600">Scheduled Time</p>
                  <p className="font-medium">
                    {format(new Date(appointment.appointment_date), "EEEE, MMMM d, yyyy")}
                  </p>
                  <p className="text-sm text-slate-600 mt-1">
                    {formatTime(appointment.appointment_date)} - {calculateEndTime()}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {appointment.provider_name && (
                <div className="flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-slate-500" />
                  <div>
                    <p className="text-sm text-slate-600">Provider</p>
                    <p className="font-medium">{appointment.provider_name}</p>
                  </div>
                </div>
              )}

              {appointment.room_id && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-500" />
                  <div>
                    <p className="text-sm text-slate-600">Room</p>
                    <p className="font-medium">{getRoomName(appointment.room_id, rooms)}</p>
                  </div>
                </div>
              )}

              {appointment.duration_actual && (
                <div>
                  <p className="text-sm text-slate-600">Duration</p>
                  <p className="font-medium">
                    {appointment.duration_actual} minutes
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Telemedicine Meeting Info */}
          {appointment.isTelemedicine && (
            <TelemedicineMeetingInfo booking={appointment} isHost={true} />
          )}

          {/* Additional Information */}
          {(appointment.notes ||
            appointment.patient_response ||
            appointment.service_parameters) && (
            <div className="space-y-3">
              <h4 className="font-medium text-slate-900">
                Additional Information
              </h4>

              {appointment.notes && (
                <div>
                  <p className="text-sm text-slate-600">Notes</p>
                  <p className="text-sm bg-slate-50 p-3 rounded-lg">
                    {appointment.notes}
                  </p>
                </div>
              )}

              {appointment.patient_response && (
                <div>
                  <p className="text-sm text-slate-600">Patient Response</p>
                  <p className="text-sm bg-slate-50 p-3 rounded-lg">
                    {appointment.patient_response}
                  </p>
                </div>
              )}

              {appointment.service_parameters &&
                Object.keys(appointment.service_parameters).length > 0 && (
                  <div>
                    <p className="text-sm text-slate-600">Service Parameters</p>
                    <div className="text-sm bg-slate-50 p-3 rounded-lg">
                      {Object.entries(appointment.service_parameters).map(
                        ([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="capitalize">
                              {key.replace("_", " ")}:
                            </span>
                            <span>{value}</span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-4 border-t">
            {appointment.status === "scheduled" && onConfirm && (
              <Button
                onClick={() => {
                  onConfirm(appointment.id);
                  onClose();
                }}
                className="gap-2 bg-blue-600 hover:bg-blue-700"
                data-testid="appointment-confirm-btn"
              >
                <ThumbsUp className="w-4 h-4" />
                Confirm
              </Button>
            )}

            {(appointment.status === "scheduled" || appointment.status === "confirmed") && onComplete && (
              <Button
                onClick={() => {
                  onComplete(appointment.id);
                  onClose();
                }}
                className="gap-2 bg-green-600 hover:bg-green-700"
                data-testid="appointment-complete-btn"
              >
                <CheckCircle className="w-4 h-4" />
                Mark Complete
              </Button>
            )}

            {onEdit && (
              <Button
                variant="outline"
                onClick={() => onEdit(appointment)}
                className="gap-2"
                data-testid="appointment-edit-btn"
              >
                <Edit className="w-4 h-4" />
                Edit
              </Button>
            )}

            {onDelete && (
              <Button
                variant="outline"
                onClick={() => onDelete(appointment.id)}
                className="gap-2 text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
                data-testid="appointment-delete-btn"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </Button>
            )}

            <Button variant="outline" onClick={onClose} className="ml-auto" data-testid="appointment-close-btn">
              Close
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
